import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 15000-15999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const formsErrors = {
  // 15xxx is forms — a fresh block, not the next free 70xx: the 7xxx range already carries a
  // duplicate (7012 twice), and 15001 is the value forms.schema.ts's shipped description for
  // /archiveTaskInspectionAndProgress already names.
  //
  // Bulk archive/un-archive: the caller held RESOURCE_MODIFY on SOME supplied ids, so the batch
  // partly succeeded. Distinct from INSUFFICIENT_PERMISSION (none did) — that difference is what
  // lets the client refresh the grid on a partial run but leave it alone on a full denial.
  FORM_ARCHIVE_PARTIAL_PERMISSION: {
    errorCode: 15001,
    errorMessage: t("Some of the selected items were skipped because you do not have the required permission"),
    isDynamic: false,
  },
  // The forms update request is internally inconsistent —
  // `addFileUploadFieldData` sent with no `file` part, without the question's
  // answer, or without a valid table row. Caller errors, so they carry a code
  // rather than being thrown as bare Errors that read as "Something went
  // wrong".
  FORM_FILE_UPLOAD_REQUEST_INVALID: {
    errorCode: 15002,
    errorMessage: t("The file upload request is invalid"),
    isDynamic: false,
  },
  // Split off the generic SOMETHING_WENT_WRONG (9000) — forms.service.ts. `createSupportTicket`-style
  // "cannot proceed with an empty template" guard, at both form-creation and form-flow-creation call
  // sites (same concept, same message family, different action).
  FORM_TEMPLATE_EMPTY_SECTIONS: {
    errorCode: 15004,
    errorMessage: t("Template should have atleast one section to be used for form creation"),
    isDynamic: false,
  },
  // Contract-linked form creation: the contract exists and is accessible, but is still a draft —
  // must be locked first. Distinct from FORM_CONTRACT_DRAFT_MODE (below), which blocks ongoing
  // actions on an already-created contract-linked form.
  FORM_CONTRACT_NOT_LOCKED: {
    errorCode: 15005,
    errorMessage: t("Contract based forms cannot be created untill contract is locked"),
    isDynamic: false,
  },
  // Contract-linked form: no action can be performed while the contract is in draft mode. Two call
  // sites (form creation, and the shared `validateContractState` gate other form actions run
  // through) — same concept, slightly different wording per site.
  FORM_CONTRACT_DRAFT_MODE: {
    errorCode: 15006,
    errorMessage: t("No actions can be performed in forms while the contract is in draft mode"),
    isDynamic: false,
  },
  // `updateTaskInspectionAndProgress` rollback action: the form is already at its first level, so
  // there is nothing to roll back to.
  FORM_ROLLBACK_FIRST_LEVEL_BLOCKED: {
    errorCode: 15007,
    errorMessage: t("You cannot roll back the first level"),
    isDynamic: false,
  },
  // `validateContractState`: the form instance being acted on is not the latest version.
  FORM_NOT_LATEST_VERSION: {
    errorCode: 15008,
    errorMessage: t(
      "This is not the latest version of this form please go to the latest version to perform any action",
    ),
    isDynamic: false,
  },
  // Form/form-flow creation: the session user is not a member of the target space. 2 call sites.
  FORM_USER_NOT_IN_SPACE: {
    errorCode: 15009,
    errorMessage: t("You are not part of this space"),
    isDynamic: false,
  },
  // 15011+ is the forms module's RESOURCE_NOT_FOUND (9003) split.
  //
  // 15011-15017 are SEVEN codes for what is really one concept — "a form or template row was not
  // found". They exist because the module's legacy copy says it seven different ways, and this
  // migration does not change user-visible text. If a copy change is ever approved, these collapse
  // into roughly two codes (one form, one template) and the singular/plural pairs go with them.
  //
  // "Form not found" — 3 call sites. Two resolve a form INSTANCE (`InspectionAndProgress`), one a
  // template row; they share this code because they share the sentence.
  FORM_NOT_FOUND: {
    errorCode: 15011,
    errorMessage: t("Form not found"),
    isDynamic: false,
  },
  // "Form does not exists" (sic) — 3 call sites in forms.contracts.service.ts.
  FORM_DOES_NOT_EXIST: {
    errorCode: 15012,
    errorMessage: t("Form does not exists"),
    isDynamic: false,
  },
  // "Form(s) does not exists" (sic) — the bulk-delete variant.
  FORMS_DO_NOT_EXIST: {
    errorCode: 15013,
    errorMessage: t("Form(s) does not exists"),
    isDynamic: false,
  },
  // "Form details not found" — `fetchFormData` came back empty inside the update transaction.
  FORM_DETAILS_NOT_FOUND: {
    errorCode: 15014,
    errorMessage: t("Form details not found"),
    isDynamic: false,
  },
  // "Form Template not found."
  FORM_TEMPLATE_NOT_FOUND: {
    errorCode: 15015,
    errorMessage: t("Form Template not found."),
    isDynamic: false,
  },
  // "Form Template(s) not found." — the bulk variant.
  FORM_TEMPLATES_NOT_FOUND: {
    errorCode: 15016,
    errorMessage: t("Form Template(s) not found."),
    isDynamic: false,
  },
  // "Template not found" — the short wording, used where a template is resolved alongside a space.
  TEMPLATE_NOT_FOUND: {
    errorCode: 15017,
    errorMessage: t("Template not found"),
    isDynamic: false,
  },
  // "Form flow not found". Deliberately NOT the pre-existing FORM_FLOW_NOT_FOUND (15010), whose
  // canonical message is the unrelated "Incorrect form flow Id".
  FORM_FLOW_ROW_NOT_FOUND: {
    errorCode: 15018,
    errorMessage: t("Form flow not found"),
    isDynamic: false,
  },
  // "Settings does not exists" (sic) — a user's saved form-creation settings row.
  FORM_USER_SETTINGS_NOT_FOUND: {
    errorCode: 15019,
    errorMessage: t("Settings does not exists"),
    isDynamic: false,
  },
  // "Item not found in form" — a `contractsFormHasItems` row. 3 call sites.
  FORM_ITEM_NOT_FOUND: {
    errorCode: 15020,
    errorMessage: t("Item not found in form"),
    isDynamic: false,
  },
  // `addFormFlowFromLibrary`: the named formFlow id does not resolve.
  FORM_FLOW_NOT_FOUND: {
    errorCode: 15010,
    errorMessage: t("Incorrect form flow Id"),
    isDynamic: false,
  },
  // 15021-15043 completes the forms module's split. Nearly all are write-failure envelopes whose
  // sentence names the specific resource that failed.
  FORM_TEMPLATE_TYPES_DELETE_FAILED: {
    errorCode: 15021,
    errorMessage: t("The template type(s) could not be deleted."),
    isDynamic: false,
  },
  FORM_TEMPLATE_TYPE_CREATE_FAILED: {
    errorCode: 15022,
    errorMessage: t("The template type could not be created."),
    isDynamic: false,
  },
  FORM_TEMPLATE_TYPE_UPDATE_FAILED: {
    errorCode: 15023,
    errorMessage: t("The template type could not be updated."),
    isDynamic: false,
  },
  FORM_TEMPLATE_TYPE_EMBEDS_NOT_UPDATED: {
    errorCode: 15024,
    errorMessage: t("The template type was updated, but the Templates embedding it were not."),
    isDynamic: false,
  },
  FORM_TEMPLATE_CREATE_FAILED: {
    errorCode: 15025,
    errorMessage: t("The template could not be created."),
    isDynamic: false,
  },
  FORM_TEMPLATES_DELETE_FAILED: {
    errorCode: 15026,
    errorMessage: t("The Form Template(s) could not be deleted."),
    isDynamic: false,
  },
  FORM_SECTION_LIBRARY_SAVE_FAILED: {
    errorCode: 15027,
    errorMessage: t("The section could not be saved to the library."),
    isDynamic: false,
  },
  FORM_SECTIONS_DELETE_FAILED: {
    errorCode: 15028,
    errorMessage: t("The selected section(s) could not be deleted."),
    isDynamic: false,
  },
  FORM_CREATE_FAILED: {
    errorCode: 15029,
    errorMessage: t("The form could not be created. Please try again."),
    isDynamic: false,
  },
  FORM_PROFILE_SIGN_NOT_SET: {
    errorCode: 15030,
    errorMessage: t("Profile sign not set"),
    isDynamic: false,
  },
  FORM_NO_SECTIONS_TO_VERIFY: {
    errorCode: 15031,
    errorMessage: t("No sections were sent to verify"),
    isDynamic: false,
  },
  FORM_NO_REVIEWS_TO_ATTACH: {
    errorCode: 15032,
    errorMessage: t("No reviews were sent to attach"),
    isDynamic: false,
  },
  FORM_NO_FORMS_TO_LINK: {
    errorCode: 15033,
    errorMessage: t("No forms were sent to link"),
    isDynamic: false,
  },
  FORM_NO_TASK_OPERATION: {
    errorCode: 15034,
    errorMessage: t("No task operation specified."),
    isDynamic: false,
  },
  FORM_NO_WBS_OPERATION: {
    errorCode: 15035,
    errorMessage: t("No WBS operation specified."),
    isDynamic: false,
  },
  FORM_NO_VALID_ACTION: {
    errorCode: 15036,
    errorMessage: t("No valid action performed"),
    isDynamic: false,
  },
  FORM_FLOW_UPDATE_FAILED: {
    errorCode: 15037,
    errorMessage: t("The form flow could not be updated."),
    isDynamic: false,
  },
  FORM_DEFAULT_FLOW_NOT_CONFIGURED: {
    errorCode: 15038,
    errorMessage: t("Default form flow is not configured"),
    isDynamic: false,
  },
  FORM_FLOW_CREATE_FAILED: {
    errorCode: 15039,
    errorMessage: t("The form flow could not be created."),
    isDynamic: false,
  },
  FORM_FLOW_DUPLICATE_FAILED: {
    errorCode: 15040,
    errorMessage: t("The form flow could not be duplicated."),
    isDynamic: false,
  },
  FORM_FLOWS_ARCHIVE_FAILED: {
    errorCode: 15041,
    errorMessage: t("The selected Form Flow(s) could not be archived."),
    isDynamic: false,
  },
  FORM_TEMPLATES_ARCHIVE_FAILED: {
    errorCode: 15042,
    errorMessage: t("The selected template(s) could not be archived."),
    isDynamic: false,
  },
  FORM_NO_FILE_PROVIDED: {
    errorCode: 15043,
    errorMessage: t("No file provided"),
    isDynamic: false,
  },
  // A deliberate anti-enumeration mask: gone vs. not-visible answered identically.
  FORM_NOT_FOUND_OR_NO_ACCESS: {
    errorCode: 15044,
    errorMessage: t("Form does not exist or you do not have permission to view it"),
    isDynamic: false,
  },
  // 15045-15056 is the forms module's NO_CHANGES_APPLIED (9005) split. The five
  // FORM_NOT_OPEN_FOR_* codes are one guard applied at five different stages; each names its own
  // stage, and the EDITING one alone covers 6 call sites.
  FORM_INACTIVE_NO_CHANGES: {
    errorCode: 15045,
    errorMessage: t("Form is In-Active now no changes are allowed"),
    isDynamic: false,
  },
  FORM_NO_WBS_OR_BOQ_ITEMS: {
    errorCode: 15046,
    errorMessage: t("This form has no WBS or BOQ items to list."),
    isDynamic: false,
  },
  FORM_ITEMS_CARRY_OVER_FAILED: {
    errorCode: 15047,
    errorMessage: t("The form items could not be carried over to the new form."),
    isDynamic: false,
  },
  FORM_NO_BOQ_ITEMS: {
    errorCode: 15048,
    errorMessage: t("This form has no BOQ items to list."),
    isDynamic: false,
  },
  FORM_TEMPLATE_UNCHANGED: {
    errorCode: 15049,
    errorMessage: t("No changes were applied to the template."),
    isDynamic: false,
  },
  FORM_NOT_OPEN_FOR_SUBMISSION: {
    errorCode: 15050,
    errorMessage: t("The form is no longer open for submission."),
    isDynamic: false,
  },
  FORM_NOT_OPEN_FOR_REJECTION: {
    errorCode: 15051,
    errorMessage: t("The form is no longer open for rejection."),
    isDynamic: false,
  },
  FORM_NOT_OPEN_FOR_APPROVAL: {
    errorCode: 15052,
    errorMessage: t("The form is no longer open for approval."),
    isDynamic: false,
  },
  FORM_NOT_OPEN_FOR_SECTION_VERIFICATION: {
    errorCode: 15053,
    errorMessage: t("The form is no longer open for section verification."),
    isDynamic: false,
  },
  FORM_NOT_OPEN_FOR_EDITING: {
    errorCode: 15054,
    errorMessage: t("The form is no longer open for editing."),
    isDynamic: false,
  },
  FORM_FLOWS_NONE_DELETED: {
    errorCode: 15055,
    errorMessage: t("No form flows were deleted."),
    isDynamic: false,
  },
  FORM_FLOWS_NONE_CREATED_FROM_LIBRARY: {
    errorCode: 15056,
    errorMessage: t("No form flows were created from the library."),
    isDynamic: false,
  },
  // 15057-15067 came off VALIDATION_FAILED (9004). Note FORM_QUANTITY_INVALID_ADD keeps TWO
  // spellings apart only by capitalisation at the call sites; they are one code here.
  FORM_QUANTITY_INVALID_SAVE: {
    errorCode: 15057,
    errorMessage: t("Invalid Quantity cannot be saved"),
    isDynamic: false,
  },
  FORM_AMOUNT_INVALID_SAVE: {
    errorCode: 15058,
    errorMessage: t("Invalid Amount cannot be saved"),
    isDynamic: false,
  },
  FORM_ITEM_VALUES_REQUIRED: {
    errorCode: 15059,
    errorMessage: t("Item values to update are required"),
    isDynamic: false,
  },
  FORM_WBS_BOQ_AND_CONTRACT_REQUIRED: {
    errorCode: 15060,
    errorMessage: t("WBS item, BOQ items and a linked contract are required"),
    isDynamic: false,
  },
  FORM_WBS_AND_CONTRACT_REQUIRED: {
    errorCode: 15061,
    errorMessage: t("WBS items and a linked contract are required"),
    isDynamic: false,
  },
  FORM_CUSTOM_QUANTITY_DETAILS_REQUIRED: {
    errorCode: 15062,
    errorMessage: t("Custom quantity details are required"),
    isDynamic: false,
  },
  FORM_ATTACHMENT_KEY_MISSING: {
    errorCode: 15064,
    errorMessage: t("Missing attachment key"),
    isDynamic: false,
  },
  FORM_UPDATE_TARGET_MISSING: {
    errorCode: 15065,
    errorMessage: t("The update named no section and question to write to."),
    isDynamic: false,
  },
  FORM_COMMENT_MISSING: {
    errorCode: 15066,
    errorMessage: t("Comment is missing"),
    isDynamic: false,
  },
  FORM_QUANTITY_INVALID_ADD_CAPS: {
    errorCode: 15067,
    errorMessage: t("Invalid Quantity cannot be added"),
    isDynamic: false,
  },
  // An `awsKey` the request refers to is not one of the files stored on that
  // question, so the caller is either racing its own delete or reaching for an
  // object it does not own.
  FORM_FILE_NOT_ON_QUESTION: {
    errorCode: 15003,
    errorMessage: t("Attachment not found on this question"),
    isDynamic: false,
  },
  // Splitting FORM_FILE_NOT_ON_QUESTION: three unrelated facts shared one code.
  FORM_TABLE_SECTION_FILE_NOT_STORED: {
    errorCode: 15068,
    errorMessage: t("Table section references a file that is not stored on it"),
    isDynamic: false,
  },
  FORM_ANSWER_FILE_NOT_ON_QUESTION: {
    errorCode: 15069,
    errorMessage: t("Answer references a file that is not stored on this question"),
    isDynamic: false,
  },
  FORM_BOQ_ITEMS_NONE_MATCHED_FOR_REMOVAL: {
    errorCode: 15074,
    errorMessage: t("No BOQ items in this form matched the ids to remove"),
    isDynamic: false,
  },
  FORM_BOQ_ITEM_QUANTITY_UPDATE_FAILED: {
    errorCode: 15075,
    errorMessage: t("The BOQ item's available quantity could not be updated"),
    isDynamic: false,
  },
  FORM_BOQ_ITEMS_NONE_ADDED: {
    errorCode: 15076,
    errorMessage: t("No BOQ items were added to the form"),
    isDynamic: false,
  },
  // CUBE-18643 kept these two apart in prose on ONE code; they are two states, so two codes.
  FORM_DELETE_BLOCKED_BY_DEPENDENCY: {
    errorCode: 15088,
    errorMessage: t("Selected forms have dependency in other forms, please delete those forms first"),
    isDynamic: false,
  },
  FORM_NONE_MATCHED_OR_NO_PERMISSION: {
    errorCode: 15089,
    errorMessage: t("No matching forms found or insufficient permissions"),
    isDynamic: false,
  },
  // validateFormFlowRequest's refusals, split off SOMETHING_WENT_WRONG. The last is a template:
  // the action's name is sent in `params`, not baked into the sentence by the backend.
  FORM_FLOW_NO_LEVELS: {
    errorCode: 15084,
    errorMessage: t("A form flow must have at least one level"),
    isDynamic: false,
  },
  FORM_FLOW_LEVEL_NO_USERS: {
    errorCode: 15085,
    errorMessage: t("Each form-flow step must have at least one assigned user"),
    isDynamic: false,
  },
  FORM_FLOW_ACTION_USERS_EMPTY: {
    errorCode: 15086,
    errorMessage: t("Form-flow level action user lists cannot be empty"),
    isDynamic: false,
  },
  FORM_FLOW_ACTION_LABEL_REQUIRED: {
    errorCode: 15087,
    errorMessage: t("{{actionName}} button label is required for a level that allows this action"),
    isDynamic: true,
    dynamicParams: ["actionName"],
  },
  // Fallback codes for the four forms sub-operation wrappers: the wrapper forwards the helper's own
  // errorCode, and falls back to one of these only when a helper refuses without naming a code.
  FORM_BOQ_QUANTITIES_UPDATE_FAILED: {
    errorCode: 15080,
    errorMessage: t("The BOQ quantities could not be updated."),
    isDynamic: false,
  },
  FORM_TASKS_UPDATE_FAILED: {
    errorCode: 15081,
    errorMessage: t("The tasks could not be updated."),
    isDynamic: false,
  },
  FORM_MILESTONE_OPERATION_MISSING: {
    errorCode: 15082,
    errorMessage: t("No milestone operation specified."),
    isDynamic: false,
  },
  FORM_WBS_ITEMS_UPDATE_FAILED: {
    errorCode: 15083,
    errorMessage: t("The WBS items could not be updated."),
    isDynamic: false,
  },
  // Forms field-flag rule messages (forms.schema.ts §7.5), split off VALIDATION_FAILED /
  // SOMETHING_WENT_WRONG. Each carries its constant's sentence verbatim.
  FORM_FIELD_FLAGS_MISSING: {
    errorCode: 15077,
    errorMessage: t(
      "Every question of a mailbox or issues form template must carry both the 'Editable in forms' and 'Is Metadata' flags.",
    ),
    isDynamic: false,
  },
  FORM_FIELD_TOGGLE_CONFLICT: {
    errorCode: 15078,
    errorMessage: t(
      "\u2018Editable in forms\u2019 and \u2018Field is Metadata\u2019 cannot be toggled off at the same time.",
    ),
    isDynamic: false,
  },
  FORM_MANDATORY_REQUIRES_EDITABLE: {
    errorCode: 15079,
    errorMessage: t("'Mandatory' cannot be enabled while 'Editable in forms' is off."),
    isDynamic: false,
  },
  FORM_NO_REVERSAL_FIELDS_FOR_TYPE: {
    errorCode: 15070,
    errorMessage: t("No reversal fields are configured for {{resourceType}} forms"),
    isDynamic: true,
    dynamicParams: ["resourceType"],
  },
  FORM_TEMPLATE_TYPES_IN_USE: {
    errorCode: 15071,
    errorMessage: t("Type(s) are already in use by Forms {{typeNames}} and hence cannot be deleted"),
    isDynamic: true,
    dynamicParams: ["typeNames"],
  },
  FORM_TEMPLATES_IN_USE: {
    errorCode: 15072,
    errorMessage: t("Templates(s) {{formNames}} are already in use and hence cannot be deleted"),
    isDynamic: true,
    dynamicParams: ["formNames"],
  },
  MBOOK_CREATION_FAILED: {
    errorCode: 15073,
    errorMessage: t("The measurement book could not be created."),
    isDynamic: false,
  },
  // SAP answers its OWN rejection text, which is the only thing that says why the MBook was refused.
  // Staging sent `Mbook creation failed ${res.errorMessage}`; this keeps that sentence verbatim with
  // SAP's text in `params.message`, so nothing the submitter used to read is lost. 15073 above stays
  // for the case where SAP supplied no reason at all.
  MBOOK_CREATION_FAILED_WITH_REASON: {
    errorCode: 15108,
    errorMessage: t("Mbook creation failed {{message}}"),
    isDynamic: true,
    dynamicParams: ["message"],
  },
  // 15090-15101: the form-submission validation accumulator and the template routes.
  FORM_SUBMIT_MANDATORY_FIELD_MISSING: {
    errorCode: 15090,
    errorMessage: t("Form submission failed: Mandatory field missing"),
    isDynamic: false,
  },
  FORM_SUBMIT_ANSWER_NOT_TIMESTAMP: {
    errorCode: 15091,
    errorMessage: t("Form submission failed: The answer should be in timeStamp"),
    isDynamic: false,
  },
  FORM_SUBMIT_DATE_MUST_BE_FUTURE: {
    errorCode: 15092,
    errorMessage: t("Form submission failed: Please select a future date and time"),
    isDynamic: false,
  },
  FORM_SUBMIT_DATE_MUST_BE_PAST: {
    errorCode: 15093,
    errorMessage: t("Form submission failed: Please select a date and time from the past"),
    isDynamic: false,
  },
  FORM_SUBMIT_DATE_OUT_OF_RANGE: {
    errorCode: 15094,
    errorMessage: t("Form submission failed: Please select a date and time within the predefined range"),
    isDynamic: false,
  },
  FORM_SUBMIT_FILES_EXCEED_LIMIT: {
    errorCode: 15095,
    errorMessage: t("Form submission failed: Files uploaded exceed the max limit set for this field"),
    isDynamic: false,
  },
  FORM_SUBMIT_FILL_ALL_FIELDS: {
    errorCode: 15096,
    errorMessage: t("Fill all fields according to the rules before submitting the form"),
    isDynamic: false,
  },
  FORM_SUBMIT_TASK_PRESET_MANDATORY: {
    errorCode: 15097,
    errorMessage: t("Please ensure tasks preset section's all mandatory fields are filled"),
    isDynamic: false,
  },
  FORM_TEMPLATE_IN_USE_CANNOT_EDIT: {
    errorCode: 15098,
    errorMessage: t("Form is already in use cannot be edited"),
    isDynamic: false,
  },
  FORM_TEMPLATE_UPDATE_FAILED_PLAIN: {
    errorCode: 15099,
    errorMessage: t("The template could not be updated."),
    isDynamic: false,
  },
  FORM_PROJECT_TEMPLATES_NONE_UPDATED: {
    errorCode: 15100,
    errorMessage: t("No templates were updated for this Project."),
    isDynamic: false,
  },
  FORM_ACTION_UNSUPPORTED: {
    errorCode: 15101,
    errorMessage: t("Unsupported action."),
    isDynamic: false,
  },
  // 15102-15106: the remaining submission checks; the three with limits carry them in params.
  FORM_SUBMIT_MIN_CHARACTERS: {
    errorCode: 15102,
    errorMessage: t("Form submission failed: Minimum {{min}} characters required"),
    isDynamic: true,
    dynamicParams: ["min"],
  },
  FORM_SUBMIT_MAX_CHARACTERS: {
    errorCode: 15103,
    errorMessage: t("Form submission failed: Maximum {{max}} characters allowed"),
    isDynamic: true,
    dynamicParams: ["max"],
  },
  FORM_SUBMIT_MIN_FILES: {
    errorCode: 15104,
    errorMessage: t("Form submission failed: Minimum {{min}} files required"),
    isDynamic: true,
    dynamicParams: ["min"],
  },
  FORM_SUBMIT_VALUE_NOT_IN_CHOICES: {
    errorCode: 15105,
    errorMessage: t("Form submission failed: Selected value should be from given choices"),
    isDynamic: false,
  },
  FORM_SUBMIT_TABLE_SECTION_MISCONFIGURED: {
    errorCode: 15106,
    errorMessage: t(
      'Form submission failed: Table section "{{section}}" is misconfigured and cannot be validated. Please contact your portal administrator.',
    ),
    isDynamic: true,
    dynamicParams: ["section"],
  },
  // 15107: a typo-d `errorMessages` field the client never read.
  FORM_ANSWER_TYPE_INVALID: {
    errorCode: 15107,
    errorMessage: t("Invalid Answer type for this field"),
    isDynamic: false,
  },
  // Two refusals staging added to the template/instance write path.
  // The failed cases are a server-built list of identifiers, not translatable prose, so the list
  // travels in `params` and only the sentence around it is translated.
  FORM_TEMPLATE_CASCADE_PARTIAL: {
    errorCode: 15000,
    errorMessage: t(
      "The template was updated, but these changes did not reach every live form: {{cases}}. Re-save the template to retry.",
    ),
    isDynamic: true,
    dynamicParams: ["cases"],
  },
  FORM_CONCURRENT_MODIFICATION: {
    errorCode: 15063,
    errorMessage: t("These form(s) are being modified by another request. Please try again."),
    isDynamic: false,
  },
  FORM_TABLE_SECTION_ROWS_REQUIRED: {
    errorCode: 15109,
    errorMessage: t("Table section rows are required"),
    isDynamic: false,
  },
  // A portal's default template is what `getDefaultFormTemplates` hands every new project, so deleting
  // one would leave those projects pointing at a template that is not live.
  FORM_TEMPLATES_DEFAULT_NOT_DELETABLE: {
    errorCode: 15110,
    errorMessage: t("Default Form Template(s) {{formNames}} cannot be deleted"),
    isDynamic: true,
    dynamicParams: ["formNames"],
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
